import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../services/api';
import { useAuthStore } from '../../store/authStore';
import { useToast } from '../../components/ui/use-toast';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  ClipboardDocumentListIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  PencilSquareIcon,
  TrashIcon,
  ChartBarIcon,
} from '@heroicons/react/24/outline';
import { X, Pill, Package, Stethoscope, BarChart2, TrendingUp } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, Legend, PieChart, Pie, Cell,
} from 'recharts';

const TABS = [
  { key: 'diagnoses', label: 'Teşhisler', icon: Stethoscope },
  { key: 'medications', label: 'İlaçlar', icon: Pill },
  { key: 'supplies', label: 'Malzemeler', icon: Package },
  { key: 'stats', label: 'İstatistikler', icon: BarChart2 },
];

const MedicalDefinitionsPage = () => {
  const { user } = useAuthStore();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [activeTab, setActiveTab] = useState('diagnoses');
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem] = useState(null);

  const isAdmin = ['super_admin', 'admin', 'supervisor', 'team_leader'].includes(user?.role);

  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">
          Tıbbi Tanımlar
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Teşhis, ilaç ve malzeme veritabanı yönetimi
        </p>
      </div>

      {/* Tabs */}
      <div className="flex space-x-1 bg-slate-100 rounded-lg p-1 mb-6 w-fit">
        {TABS.map((tab) => {
          const Icon = tab.icon;
          const active = activeTab === tab.key;
          return (
            <button
              key={tab.key}
              onClick={() => { setActiveTab(tab.key); setSearch(''); }}
              className={`flex items-center space-x-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
                active
                  ? 'bg-white text-medical-700 shadow-sm'
                  : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              <Icon className="h-4 w-4" />
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>

      {/* Content per tab */}
      {activeTab === 'diagnoses' && (
        <DiagnosesTab
          search={search}
          setSearch={setSearch}
          isAdmin={isAdmin}
          showModal={showModal}
          setShowModal={setShowModal}
          editItem={editItem}
          setEditItem={setEditItem}
        />
      )}
      {activeTab === 'medications' && (
        <MedicationsTab
          search={search}
          setSearch={setSearch}
          isAdmin={isAdmin}
          showModal={showModal}
          setShowModal={setShowModal}
          editItem={editItem}
          setEditItem={setEditItem}
        />
      )}
      {activeTab === 'supplies' && (
        <SuppliesTab
          search={search}
          setSearch={setSearch}
          isAdmin={isAdmin}
          showModal={showModal}
          setShowModal={setShowModal}
          editItem={editItem}
          setEditItem={setEditItem}
        />
      )}
      {activeTab === 'stats' && <StatsTab />}
    </div>
  );
};

// ═══════════════════════════════════════════════════════
//  TEŞHİSLER TAB
// ═══════════════════════════════════════════════════════

function DiagnosesTab({ search, setSearch, isAdmin, showModal, setShowModal, editItem, setEditItem }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: diagnosesResp, isLoading } = useQuery({
    queryKey: ['medical-diagnoses'],
    queryFn: () => api.get('/medical/diagnoses').then(r => r.data),
  });

  const items = diagnosesResp?.data || [];

  const filtered = useMemo(() => {
    if (!search) return items;
    const q = search.toLowerCase();
    return items.filter(d => d.code.toLowerCase().includes(q) || d.name.toLowerCase().includes(q));
  }, [items, search]);

  const deleteMutation = useMutation({
    mutationFn: (code) => api.delete(`/medical/diagnoses/${encodeURIComponent(code)}`),
    onSuccess: () => {
      queryClient.invalidateQueries(['medical-diagnoses']);
      toast({ title: 'Başarılı', description: 'Teşhis silindi' });
    },
    onError: (err) => toast({ title: 'Hata', description: err.response?.data?.message || 'Silinemedi', variant: 'destructive' }),
  });

  return (
    <>
      <TableToolbar
        search={search}
        setSearch={setSearch}
        placeholder="Kod veya isim ile ara..."
        isAdmin={isAdmin}
        onAdd={() => { setEditItem(null); setShowModal(true); }}
      />

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50">
              <th className="px-6 py-3 text-left font-semibold text-slate-600 w-28">KOD</th>
              <th className="px-6 py-3 text-left font-semibold text-slate-600 w-64">TEŞHİS ADI</th>
              <th className="px-6 py-3 text-left font-semibold text-slate-600">AÇIKLAMA</th>
              {isAdmin && <th className="px-6 py-3 text-right font-semibold text-slate-600 w-32">İŞLEMLER</th>}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={isAdmin ? 4 : 3} className="text-center py-12 text-slate-400">Yükleniyor...</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={isAdmin ? 4 : 3} className="text-center py-12 text-slate-400">Kayıt bulunamadı</td></tr>
            ) : filtered.map((d) => (
              <tr key={d.code} className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors">
                <td className="px-6 py-3 font-medium text-medical-700">{d.code}</td>
                <td className="px-6 py-3 font-medium text-slate-800">{d.name}</td>
                <td className="px-6 py-3 text-slate-500">{d.description || '-'}</td>
                {isAdmin && (
                  <td className="px-6 py-3 text-right">
                    <div className="flex justify-end space-x-1">
                      <button onClick={() => { setEditItem(d); setShowModal(true); }} className="p-1.5 rounded-md text-slate-400 hover:text-blue-600 hover:bg-blue-50 transition-colors">
                        <PencilSquareIcon className="h-4 w-4" />
                      </button>
                      <button onClick={() => { if (confirm('Bu teşhisi silmek istediğinize emin misiniz?')) deleteMutation.mutate(d.code); }} className="p-1.5 rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors">
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showModal && (
        <DiagnosisModal
          item={editItem}
          onClose={() => { setShowModal(false); setEditItem(null); }}
          onSuccess={() => { setShowModal(false); setEditItem(null); queryClient.invalidateQueries(['medical-diagnoses']); }}
        />
      )}
    </>
  );
}

function DiagnosisModal({ item, onClose, onSuccess }) {
  const { toast } = useToast();
  const isEdit = !!item;
  const [form, setForm] = useState({
    code: item?.code || '',
    name: item?.name || '',
    description: item?.description || '',
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.code || !form.name) {
      toast({ title: 'Hata', description: 'Kod ve isim zorunludur', variant: 'destructive' });
      return;
    }
    setLoading(true);
    try {
      if (isEdit) {
        await api.put(`/medical/diagnoses/${encodeURIComponent(item.code)}`, form);
      } else {
        await api.post('/medical/diagnoses', form);
      }
      toast({ title: 'Başarılı', description: isEdit ? 'Teşhis güncellendi' : 'Teşhis eklendi' });
      onSuccess();
    } catch (err) {
      toast({ title: 'Hata', description: err.response?.data?.message || 'İşlem başarısız', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal title={isEdit ? 'Teşhis Düzenle' : 'Yeni Teşhis Ekle'} onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-1.5">
          <Label>Kod (ICD-10) *</Label>
          <Input
            value={form.code}
            onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
            placeholder="Örn: R51"
            disabled={isEdit}
            className={isEdit ? 'bg-slate-50' : ''}
          />
        </div>
        <div className="space-y-1.5">
          <Label>Teşhis Adı *</Label>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Örn: Baş Ağrısı" />
        </div>
        <div className="space-y-1.5">
          <Label>Açıklama</Label>
          <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Açıklama (opsiyonel)" rows={3} />
        </div>
        <ModalActions loading={loading} onClose={onClose} submitText={isEdit ? 'Güncelle' : 'Ekle'} />
      </form>
    </Modal>
  );
}

// ═══════════════════════════════════════════════════════
//  İLAÇLAR TAB
// ═══════════════════════════════════════════════════════

function MedicationsTab({ search, setSearch, isAdmin, showModal, setShowModal, editItem, setEditItem }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: medsResp, isLoading } = useQuery({
    queryKey: ['medical-medications'],
    queryFn: () => api.get('/medical/medications').then(r => r.data),
  });

  const items = medsResp?.data || [];

  const filtered = useMemo(() => {
    if (!search) return items;
    const q = search.toLowerCase();
    return items.filter(m => m.name.toLowerCase().includes(q) || (m.form || '').toLowerCase().includes(q));
  }, [items, search]);

  const deleteMutation = useMutation({
    mutationFn: (id) => api.delete(`/medical/medications/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries(['medical-medications']);
      toast({ title: 'Başarılı', description: 'İlaç silindi' });
    },
    onError: (err) => toast({ title: 'Hata', description: err.response?.data?.message || 'Silinemedi', variant: 'destructive' }),
  });

  return (
    <>
      <TableToolbar
        search={search}
        setSearch={setSearch}
        placeholder="İlaç adı veya formu ile ara..."
        isAdmin={isAdmin}
        onAdd={() => { setEditItem(null); setShowModal(true); }}
      />

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50">
              <th className="px-6 py-3 text-left font-semibold text-slate-600">İLAÇ ADI</th>
              <th className="px-6 py-3 text-left font-semibold text-slate-600 w-48">FORM</th>
              <th className="px-6 py-3 text-left font-semibold text-slate-600 w-48">VARSAYILAN DOZ</th>
              {isAdmin && <th className="px-6 py-3 text-right font-semibold text-slate-600 w-32">İŞLEMLER</th>}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={isAdmin ? 4 : 3} className="text-center py-12 text-slate-400">Yükleniyor...</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={isAdmin ? 4 : 3} className="text-center py-12 text-slate-400">Kayıt bulunamadı</td></tr>
            ) : filtered.map((m) => (
              <tr key={m.id} className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors">
                <td className="px-6 py-3 font-medium text-slate-800">{m.name}</td>
                <td className="px-6 py-3 text-slate-500">{m.form || '-'}</td>
                <td className="px-6 py-3 text-slate-500">{m.default_dose || '-'}</td>
                {isAdmin && (
                  <td className="px-6 py-3 text-right">
                    <div className="flex justify-end space-x-1">
                      <button onClick={() => { setEditItem(m); setShowModal(true); }} className="p-1.5 rounded-md text-slate-400 hover:text-blue-600 hover:bg-blue-50 transition-colors">
                        <PencilSquareIcon className="h-4 w-4" />
                      </button>
                      <button onClick={() => { if (confirm('Bu ilacı silmek istediğinize emin misiniz?')) deleteMutation.mutate(m.id); }} className="p-1.5 rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors">
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showModal && (
        <MedicationModal
          item={editItem}
          onClose={() => { setShowModal(false); setEditItem(null); }}
          onSuccess={() => { setShowModal(false); setEditItem(null); queryClient.invalidateQueries(['medical-medications']); }}
        />
      )}
    </>
  );
}

function MedicationModal({ item, onClose, onSuccess }) {
  const { toast } = useToast();
  const isEdit = !!item;
  const [form, setForm] = useState({
    name: item?.name || '',
    form: item?.form || '',
    default_dose: item?.default_dose || '',
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name) {
      toast({ title: 'Hata', description: 'İlaç adı zorunludur', variant: 'destructive' });
      return;
    }
    setLoading(true);
    try {
      if (isEdit) {
        await api.put(`/medical/medications/${item.id}`, form);
      } else {
        await api.post('/medical/medications', form);
      }
      toast({ title: 'Başarılı', description: isEdit ? 'İlaç güncellendi' : 'İlaç eklendi' });
      onSuccess();
    } catch (err) {
      toast({ title: 'Hata', description: err.response?.data?.message || 'İşlem başarısız', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal title={isEdit ? 'İlaç Düzenle' : 'Yeni İlaç Ekle'} onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-1.5">
          <Label>İlaç Adı *</Label>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Örn: Parol" />
        </div>
        <div className="space-y-1.5">
          <Label>Form</Label>
          <Input value={form.form} onChange={(e) => setForm({ ...form, form: e.target.value })} placeholder="Örn: Tablet, Ampul" />
        </div>
        <div className="space-y-1.5">
          <Label>Varsayılan Doz</Label>
          <Input value={form.default_dose} onChange={(e) => setForm({ ...form, default_dose: e.target.value })} placeholder="Örn: 500mg" />
        </div>
        <ModalActions loading={loading} onClose={onClose} submitText={isEdit ? 'Güncelle' : 'Ekle'} />
      </form>
    </Modal>
  );
}

// ═══════════════════════════════════════════════════════
//  MALZEMELER TAB
// ═══════════════════════════════════════════════════════

function SuppliesTab({ search, setSearch, isAdmin, showModal, setShowModal, editItem, setEditItem }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: suppliesResp, isLoading } = useQuery({
    queryKey: ['medical-supplies'],
    queryFn: () => api.get('/medical/supplies').then(r => r.data),
  });

  const items = suppliesResp?.data || [];

  const filtered = useMemo(() => {
    if (!search) return items;
    const q = search.toLowerCase();
    return items.filter(s => s.name.toLowerCase().includes(q) || (s.unit || '').toLowerCase().includes(q));
  }, [items, search]);

  const deleteMutation = useMutation({
    mutationFn: (id) => api.delete(`/medical/supplies/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries(['medical-supplies']);
      toast({ title: 'Başarılı', description: 'Malzeme silindi' });
    },
    onError: (err) => toast({ title: 'Hata', description: err.response?.data?.message || 'Silinemedi', variant: 'destructive' }),
  });

  return (
    <>
      <TableToolbar
        search={search}
        setSearch={setSearch}
        placeholder="Malzeme adı veya birim ile ara..."
        isAdmin={isAdmin}
        onAdd={() => { setEditItem(null); setShowModal(true); }}
      />

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50">
              <th className="px-6 py-3 text-left font-semibold text-slate-600">MALZEME ADI</th>
              <th className="px-6 py-3 text-left font-semibold text-slate-600 w-48">BİRİM</th>
              {isAdmin && <th className="px-6 py-3 text-right font-semibold text-slate-600 w-32">İŞLEMLER</th>}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={isAdmin ? 3 : 2} className="text-center py-12 text-slate-400">Yükleniyor...</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={isAdmin ? 3 : 2} className="text-center py-12 text-slate-400">Kayıt bulunamadı</td></tr>
            ) : filtered.map((s) => (
              <tr key={s.id} className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors">
                <td className="px-6 py-3 font-medium text-slate-800">{s.name}</td>
                <td className="px-6 py-3 text-slate-500">{s.unit || '-'}</td>
                {isAdmin && (
                  <td className="px-6 py-3 text-right">
                    <div className="flex justify-end space-x-1">
                      <button onClick={() => { setEditItem(s); setShowModal(true); }} className="p-1.5 rounded-md text-slate-400 hover:text-blue-600 hover:bg-blue-50 transition-colors">
                        <PencilSquareIcon className="h-4 w-4" />
                      </button>
                      <button onClick={() => { if (confirm('Bu malzemeyi silmek istediğinize emin misiniz?')) deleteMutation.mutate(s.id); }} className="p-1.5 rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors">
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showModal && (
        <SupplyModal
          item={editItem}
          onClose={() => { setShowModal(false); setEditItem(null); }}
          onSuccess={() => { setShowModal(false); setEditItem(null); queryClient.invalidateQueries(['medical-supplies']); }}
        />
      )}
    </>
  );
}

function SupplyModal({ item, onClose, onSuccess }) {
  const { toast } = useToast();
  const isEdit = !!item;
  const [form, setForm] = useState({
    name: item?.name || '',
    unit: item?.unit || '',
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name) {
      toast({ title: 'Hata', description: 'Malzeme adı zorunludur', variant: 'destructive' });
      return;
    }
    setLoading(true);
    try {
      if (isEdit) {
        await api.put(`/medical/supplies/${item.id}`, form);
      } else {
        await api.post('/medical/supplies', form);
      }
      toast({ title: 'Başarılı', description: isEdit ? 'Malzeme güncellendi' : 'Malzeme eklendi' });
      onSuccess();
    } catch (err) {
      toast({ title: 'Hata', description: err.response?.data?.message || 'İşlem başarısız', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal title={isEdit ? 'Malzeme Düzenle' : 'Yeni Malzeme Ekle'} onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-1.5">
          <Label>Malzeme Adı *</Label>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Örn: Sargı Bezi" />
        </div>
        <div className="space-y-1.5">
          <Label>Birim</Label>
          <Input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} placeholder="Örn: Adet, Kutu" />
        </div>
        <ModalActions loading={loading} onClose={onClose} submitText={isEdit ? 'Güncelle' : 'Ekle'} />
      </form>
    </Modal>
  );
}

// ═══════════════════════════════════════════════════════
//  SHARED COMPONENTS
// ═══════════════════════════════════════════════════════

function TableToolbar({ search, setSearch, placeholder, isAdmin, onAdd }) {
  return (
    <div className="flex items-center justify-between mb-4 gap-4">
      <div className="relative flex-1 max-w-md">
        <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={placeholder}
          className="pl-9 h-10"
        />
      </div>
      {isAdmin && (
        <Button onClick={onAdd} className="bg-medical-600 hover:bg-medical-700 shadow-sm">
          <PlusIcon className="h-4 w-4 mr-2" />
          Yeni Ekle
        </Button>
      )}
    </div>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-md animate-in fade-in zoom-in-95 duration-200">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-bold text-slate-900">{title}</h2>
          <button onClick={onClose} className="rounded-lg p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors">
            <X className="h-5 w-5" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function ModalActions({ loading, onClose, submitText }) {
  return (
    <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
      <Button type="button" variant="outline" onClick={onClose} className="h-10">
        İptal
      </Button>
      <Button type="submit" disabled={loading} className="h-10 bg-medical-600 hover:bg-medical-700 shadow-sm">
        {loading ? 'Kaydediliyor...' : submitText}
      </Button>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
//  İSTATİSTİKLER TAB
// ═══════════════════════════════════════════════════════════

const CHART_COLORS = ['#6366f1', '#22d3ee', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316', '#84cc16'];

const today = new Date();
const formatDateInput = (d) => d.toISOString().split('T')[0];
const defaultStart = formatDateInput(new Date(today.getFullYear(), 0, 1)); // 1 Ocak
const defaultEnd = formatDateInput(today);

function StatsTab() {
  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(defaultEnd);
  const [activeSection, setActiveSection] = useState('diagnoses');

  const { data: statsResp, isLoading, refetch } = useQuery({
    queryKey: ['medical-stats', startDate, endDate],
    queryFn: () => api.get('/medical/stats', { params: { startDate, endDate } }).then(r => r.data),
    staleTime: 60000,
  });

  const stats = statsResp?.data;

  const SECTIONS = [
    { key: 'diagnoses', label: 'Teşhisler', icon: Stethoscope, color: '#6366f1', dataKey: 'topDiagnoses', nameKey: 'name', totalKey: 'totalDiagnoses' },
    { key: 'medications', label: 'İlaçlar', icon: Pill, color: '#10b981', dataKey: 'topMedications', nameKey: 'name', totalKey: 'totalMedications' },
    { key: 'supplies', label: 'Malzemeler', icon: Package, color: '#f59e0b', dataKey: 'topSupplies', nameKey: 'name', totalKey: 'totalSupplies' },
  ];

  const activeSecDef = SECTIONS.find(s => s.key === activeSection);
  const chartData = stats ? (stats[activeSecDef.dataKey] || []).slice(0, 10).map(item => ({
    name: item.name?.length > 22 ? item.name.substring(0, 22) + '…' : item.name,
    fullName: item.name,
    count: item.count,
    code: item.code,
    totalQty: item.totalQty,
  })) : [];

  const pieData = chartData.slice(0, 6);

  return (
    <div className="space-y-6">
      {/* Date range filter */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 flex flex-wrap items-end gap-3">
        <div>
          <label className="block text-xs font-medium text-slate-500 mb-1">Başlangıç Tarihi</label>
          <input
            type="date"
            value={startDate}
            onChange={e => setStartDate(e.target.value)}
            className="border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-500 mb-1">Bitiş Tarihi</label>
          <input
            type="date"
            value={endDate}
            onChange={e => setEndDate(e.target.value)}
            className="border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
          />
        </div>
        <Button onClick={() => refetch()} className="h-9 bg-indigo-600 hover:bg-indigo-700 text-white text-sm px-4">
          Güncelle
        </Button>
      </div>

      {isLoading ? (
        <div className="flex flex-col items-center justify-center py-20 text-slate-400">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-500 mb-3" />
          <span className="text-sm">Veriler yükleniyor...</span>
        </div>
      ) : !stats ? null : (
        <>
          {/* Summary cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {[
              { label: 'Tıbbi Kayıtlı Vaka', value: stats.totalCasesWithMedical, color: 'bg-indigo-50 border-indigo-100', textColor: 'text-indigo-700', icon: ClipboardDocumentListIcon },
              { label: 'Toplam Teşhis', value: stats.summary.totalDiagnoses, color: 'bg-violet-50 border-violet-100', textColor: 'text-violet-700', icon: Stethoscope },
              { label: 'Toplam İlaç', value: stats.summary.totalMedications, color: 'bg-emerald-50 border-emerald-100', textColor: 'text-emerald-700', icon: Pill },
              { label: 'Toplam Malzeme', value: stats.summary.totalSupplies, color: 'bg-amber-50 border-amber-100', textColor: 'text-amber-700', icon: Package },
            ].map(card => {
              const Icon = card.icon;
              return (
                <div key={card.label} className={`rounded-xl border p-4 ${card.color} flex items-center gap-3`}>
                  <div className={`rounded-lg p-2 bg-white shadow-sm`}>
                    <Icon className={`h-5 w-5 ${card.textColor}`} />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 font-medium">{card.label}</p>
                    <p className={`text-xl font-bold ${card.textColor}`}>{card.value?.toLocaleString('tr-TR')}</p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Section toggle */}
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="flex border-b border-slate-100">
              {SECTIONS.map(s => {
                const Icon = s.icon;
                return (
                  <button
                    key={s.key}
                    onClick={() => setActiveSection(s.key)}
                    className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium transition-colors ${
                      activeSection === s.key
                        ? 'bg-slate-50 text-slate-900 border-b-2 border-indigo-500'
                        : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    {s.label}
                  </button>
                );
              })}
            </div>

            <div className="p-5">
              {chartData.length === 0 ? (
                <div className="text-center py-10 text-slate-400 text-sm">
                  Bu tarih aralığında veri bulunamadı.
                </div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Bar chart - top 10 */}
                  <div className="lg:col-span-2">
                    <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                      <BarChart2 className="h-4 w-4 text-indigo-500" />
                      En Çok Kullanılan {activeSecDef.label} (İlk 10)
                    </h3>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={chartData} layout="vertical" margin={{ left: 10, right: 20, top: 5, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f1f5f9" />
                        <XAxis type="number" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                        <YAxis
                          type="category"
                          dataKey="name"
                          width={150}
                          tick={{ fontSize: 11, fill: '#64748b' }}
                          axisLine={false}
                          tickLine={false}
                        />
                        <Tooltip
                          formatter={(value, name, props) => [value + ' vaka', props.payload.fullName || props.payload.name]}
                          contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '12px' }}
                        />
                        <Bar dataKey="count" fill={activeSecDef.color} radius={[0, 4, 4, 0]}>
                          {chartData.map((_, index) => (
                            <Cell key={index} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Pie chart - top 6 */}
                  <div>
                    <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                      <TrendingUp className="h-4 w-4 text-indigo-500" />
                      Dağılım (İlk 6)
                    </h3>
                    <ResponsiveContainer width="100%" height={220}>
                      <PieChart>
                        <Pie
                          data={pieData}
                          cx="50%"
                          cy="50%"
                          innerRadius={55}
                          outerRadius={85}
                          paddingAngle={3}
                          dataKey="count"
                        >
                          {pieData.map((_, index) => (
                            <Cell key={index} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip
                          formatter={(value, name, props) => [value + ' vaka', props.payload.fullName || props.payload.name]}
                          contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '12px' }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                    {/* Legend */}
                    <div className="space-y-1 mt-1">
                      {pieData.map((item, index) => (
                        <div key={index} className="flex items-center gap-2 text-xs text-slate-600">
                          <span className="inline-block w-3 h-3 rounded-sm flex-shrink-0" style={{ backgroundColor: CHART_COLORS[index % CHART_COLORS.length] }} />
                          <span className="truncate flex-1">{item.fullName}</span>
                          <span className="font-semibold text-slate-800 flex-shrink-0">{item.count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Monthly trend */}
          {stats.monthly?.length > 0 && (
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <h3 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-indigo-500" />
                Aylık Tıbbi Kayıt Trendi
              </h3>
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={stats.monthly} margin={{ left: 0, right: 10, top: 5, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis
                    dataKey="month"
                    tick={{ fontSize: 11, fill: '#94a3b8' }}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={m => {
                      const [y, mo] = m.split('-');
                      return ['Oca','Şub','Mar','Nis','May','Haz','Tem','Ağu','Eyl','Eki','Kas','Ara'][parseInt(mo,10)-1] + ' ' + y.slice(2);
                    }}
                  />
                  <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                  <Tooltip
                    contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '12px' }}
                    labelFormatter={m => {
                      const [y, mo] = m.split('-');
                      return ['Ocak','Şubat','Mart','Nisan','Mayıs','Haziran','Temmuz','Ağustos','Eylül','Ekim','Kasım','Aralık'][parseInt(mo,10)-1] + ' ' + y;
                    }}
                  />
                  <Legend formatter={v => v === 'diagnoses' ? 'Teşhisler' : v === 'medications' ? 'İlaçlar' : 'Malzemeler'} />
                  <Line type="monotone" dataKey="diagnoses" stroke="#6366f1" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                  <Line type="monotone" dataKey="medications" stroke="#10b981" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                  <Line type="monotone" dataKey="supplies" stroke="#f59e0b" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Top items table */}
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
              {React.createElement(activeSecDef.icon, { className: 'h-4 w-4', style: { color: activeSecDef.color } })}
              {activeSecDef.label} — Detaylı Liste
            </h3>
            {chartData.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-4">Veri bulunamadı.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs font-medium text-slate-500 border-b border-slate-100">
                      <th className="pb-2 pr-4">#</th>
                      {activeSection === 'diagnoses' && <th className="pb-2 pr-4">Kod</th>}
                      <th className="pb-2 pr-4">Ad</th>
                      <th className="pb-2 pr-4 text-right">Kullanım</th>
                      {activeSection === 'supplies' && <th className="pb-2 text-right">Toplam Miktar</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {(stats[activeSecDef.dataKey] || []).slice(0, 15).map((item, idx) => (
                      <tr key={idx} className="border-b border-slate-50 hover:bg-slate-50 transition-colors">
                        <td className="py-2 pr-4 text-slate-400 font-mono text-xs">{idx + 1}</td>
                        {activeSection === 'diagnoses' && <td className="py-2 pr-4 font-mono text-xs text-indigo-600">{item.code}</td>}
                        <td className="py-2 pr-4 text-slate-700">{item.name}</td>
                        <td className="py-2 pr-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <div className="w-16 bg-slate-100 rounded-full h-1.5 overflow-hidden">
                              <div
                                className="h-1.5 rounded-full"
                                style={{
                                  width: `${Math.round((item.count / ((stats[activeSecDef.dataKey] || [])[0]?.count || 1)) * 100)}%`,
                                  backgroundColor: activeSecDef.color,
                                }}
                              />
                            </div>
                            <span className="font-semibold text-slate-800 w-8 text-right">{item.count}</span>
                          </div>
                        </td>
                        {activeSection === 'supplies' && (
                          <td className="py-2 text-right text-slate-600">{item.totalQty?.toLocaleString('tr-TR')}</td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default MedicalDefinitionsPage;
